import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { buyersAPI } from '@/lib/api-client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isDocuSignEnvelopeId(value: string): boolean {
  return UUID_REGEX.test(value.trim())
}

const buyerFormSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  document: z.string().min(1, 'Documento e obrigatorio'),
  email: z.string().email('E-mail invalido').optional().or(z.literal('')),
  phone: z.string().optional(),
  address: z.string().optional(),
  contractUrl: z.string().optional().or(z.literal('')),
})

type BuyerFormValues = z.infer<typeof buyerFormSchema>

interface BuyerFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  buyer?: any
}

export function BuyerFormDialog({
  open,
  onOpenChange,
  buyer,
}: BuyerFormDialogProps) {
  const queryClient = useQueryClient()
  const isEdit = !!buyer

  const [contractType, setContractType] = useState<'link' | 'docusign'>(
    buyer?.contractUrl && isDocuSignEnvelopeId(buyer.contractUrl) ? 'docusign' : 'link'
  )

  const form = useForm<BuyerFormValues>({
    resolver: zodResolver(buyerFormSchema),
    defaultValues: buyer
      ? {
          name: buyer.name,
          document: buyer.document,
          email: buyer.email || '',
          phone: buyer.phone || '',
          address: buyer.address || '',
          contractUrl: buyer.contractUrl || '',
        }
      : {
          name: '',
          document: '',
          email: '',
          phone: '',
          address: '',
          contractUrl: '',
        },
  })

  const mutation = useMutation({
    mutationFn: async (values: BuyerFormValues) => {
      let contractUrlValue = values.contractUrl || undefined
      if (contractUrlValue && contractType === 'docusign') {
        // Validate UUID format
        if (!isDocuSignEnvelopeId(contractUrlValue)) {
          throw new Error('Envelope ID invalido. Use o formato UUID (ex: 0863169C-A429-4965-A5E3-072682758BB1)')
        }
        contractUrlValue = contractUrlValue.trim().toUpperCase()
      }

      const payload: any = {
        name: values.name,
        document: values.document.replace(/\D/g, ''),
        email: values.email || undefined,
        phone: values.phone || undefined,
        address: values.address || undefined,
        contractUrl: contractUrlValue,
      }

      if (isEdit) {
        return buyersAPI.update(buyer.id, payload)
      }
      return buyersAPI.create(payload)
    },
    onSuccess: () => {
      toast.success(
        isEdit ? 'Comprador atualizado!' : 'Comprador cadastrado!'
      )
      queryClient.invalidateQueries({ queryKey: ['buyers'] })
      if (isEdit) {
        queryClient.invalidateQueries({
          queryKey: ['buyer', buyer.id],
        })
      }
      onOpenChange(false)
      form.reset()
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const onSubmit = (values: BuyerFormValues) => {
    mutation.mutate(values)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar Comprador' : 'Novo Comprador'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Atualize as informacoes do comprador.'
              : 'Preencha as informacoes para cadastrar um novo comprador.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-neutral-700">
              Informacoes Pessoais
            </h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  {...form.register('name')}
                  placeholder="Nome do comprador"
                />
                {form.formState.errors.name && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="document">Documento (CPF/CNPJ) *</Label>
                <Input
                  id="document"
                  {...form.register('document')}
                  placeholder="000.000.000-00 ou 00.000.000/0001-00"
                />
                {form.formState.errors.document && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.document.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  {...form.register('email')}
                  placeholder="email@exemplo.com"
                />
                {form.formState.errors.email && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  {...form.register('phone')}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="address">Endereco</Label>
              <Input
                id="address"
                {...form.register('address')}
                placeholder="Endereco completo"
              />
            </div>
          </div>

          <Separator />

          {/* Contract */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-neutral-700">Contrato</h3>

            <div className="grid gap-4 sm:grid-cols-[180px_1fr]">
              <div>
                <Label>Tipo</Label>
                <Select
                  value={contractType}
                  onValueChange={(v) => {
                    setContractType(v as 'link' | 'docusign')
                    form.setValue('contractUrl', '')
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="link">Link</SelectItem>
                    <SelectItem value="docusign">DocuSign Envelope ID</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="contractUrl">
                  {contractType === 'docusign' ? 'Envelope ID' : 'URL do Contrato'}
                </Label>
                <Input
                  id="contractUrl"
                  {...form.register('contractUrl')}
                  placeholder={
                    contractType === 'docusign'
                      ? '0863169C-A429-4965-A5E3-072682758BB1'
                      : 'https://...'
                  }
                />
                {contractType === 'docusign' && (
                  <p className="text-xs text-neutral-400 mt-1">
                    Cole apenas o Envelope ID. A URL sera montada automaticamente.
                  </p>
                )}
                {form.formState.errors.contractUrl && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.contractUrl.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isEdit ? 'Salvar' : 'Cadastrar Comprador'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
